'use client'

import Link from 'next/link'
import { useState } from 'react'
import { usePathname } from 'next/navigation'

type Coupon = {
  id: string
  name: string
  code: string
  description: string | null
  redemptionType: 'PERCENT_DISCOUNT' | 'FIXED_DISCOUNT' | 'WALLET_CREDIT' | 'FREE_MINUTES'
  redemptionValue: string
  validUntil: string
  redeemInstructions: string | null
}

function readCsrfCookie(): string | null {
  const m = document.cookie.match(/(?:^|; )__Host-buyer\.csrf=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

function fmtValue(c: Coupon): string {
  const n = Number(c.redemptionValue)
  if (c.redemptionType === 'PERCENT_DISCOUNT') return `${n}% off`
  if (c.redemptionType === 'FIXED_DISCOUNT') return `LKR ${n.toLocaleString()} off`
  return c.redemptionType
}

export function OffersBlock({
  coupons,
  signedIn,
}: {
  coupons: Coupon[]
  signedIn: boolean
}) {
  if (coupons.length === 0) return null
  return (
    <div className="mb-4">
      <div className="text-[10px] text-text-3 font-mono uppercase tracking-widest mb-2">
        Available offers
      </div>
      <div className="space-y-2">
        {coupons.map((c) => (
          <OfferRow key={c.id} coupon={c} signedIn={signedIn} />
        ))}
      </div>
    </div>
  )
}

function OfferRow({ coupon, signedIn }: { coupon: Coupon; signedIn: boolean }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [claimedCode, setClaimedCode] = useState<string | null>(null)
  const pathname = usePathname()

  async function claim() {
    if (busy) return
    setBusy(true)
    setError(null)
    const csrf = readCsrfCookie()
    if (!csrf) {
      setError('Sign in again to claim.')
      setBusy(false)
      return
    }
    try {
      const res = await fetch(`/api/v1/marketplace/coupons/${coupon.id}/claim`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf },
      })
      const data = (await res.json().catch(() => null)) as
        | { issuance?: { couponCode: string }; error?: string }
        | null
      if (!res.ok || !data?.issuance) {
        setError(data?.error ?? 'Could not claim.')
        setBusy(false)
        return
      }
      setClaimedCode(data.issuance.couponCode)
    } catch {
      setError('Network error. Try again.')
    } finally {
      setBusy(false)
    }
  }

  if (claimedCode) {
    return (
      <div className="bg-green/10 border border-green/30 rounded p-3">
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className="min-w-0">
            <div className="text-xs text-text-3 font-mono">Your code</div>
            <div className="font-mono text-lg font-bold tracking-widest text-green">{claimedCode}</div>
          </div>
          <Link
            href="/me/coupons"
            className="text-[10px] text-cyan hover:underline shrink-0"
          >
            View all →
          </Link>
        </div>
        <div className="text-xs text-text-2">
          Show this code when you connect with the vendor offline.
        </div>
      </div>
    )
  }

  return (
    <div className="bg-navy-2 border border-amber/30 rounded p-3">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{coupon.name}</div>
          {coupon.description && (
            <div className="text-xs text-text-3 truncate">{coupon.description}</div>
          )}
        </div>
        <div className="text-amber text-sm font-semibold shrink-0">
          {fmtValue(coupon)}
        </div>
      </div>
      {error && (
        <div className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded px-2 py-1 mb-2">
          {error}
        </div>
      )}
      {signedIn ? (
        <button
          type="button"
          onClick={claim}
          disabled={busy}
          className="w-full px-3 py-1.5 bg-amber/90 text-navy text-xs font-semibold rounded hover:brightness-95 disabled:opacity-40"
        >
          {busy ? 'Claiming…' : 'Claim offer'}
        </button>
      ) : (
        <Link
          href={`/auth/buyer-signin?next=${encodeURIComponent(pathname)}`}
          className="block w-full text-center px-3 py-1.5 bg-amber/90 text-navy text-xs font-semibold rounded hover:brightness-95"
        >
          Sign in to claim
        </Link>
      )}
    </div>
  )
}
